/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Arrangementer pro (owner 2026-10-31; EVENTS_PRO.md): the parts Events.tsx and Påmelding add to an event.
 *
 *   useConflicts / ConflictNote   «Overlapper med «X» kl. 18:00 på samme sted» — amber, a warning, never a block
 *   EventGuests                   «Gjester» from Kontakter: role line, «Vis i appen» (behind contacts_directory)
 *   EventFeedback                 the survey: «★ 4,3 (12)» and the comments, never who wrote them
 *   TicketList                    «Billetter» at the door: who bought, how many, check in (behind event_tickets)
 *   oreFromKr / krFromOre         a price typed in kroner, sent in øre
 *
 * Every result and error is a toast (useMsg). The server decides what may be done (its `can`) and says it again on
 * each request.
 */
import React, { useEffect, useState } from 'react';
import { call, errText, patch, post, send, type Body } from '../lib/api';
import { useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { kr } from '../lib/format';
import { Btn, Field, Tag, TwoStep } from './ui';

/** «150» or «150,50» kroner → øre; '' → null (free); anything else → NaN (the field says so). */
export function oreFromKr(v: string): number | null {
  const s = String(v || '').trim().replace(/\s/g, '').replace(/kr$/i, '');
  if (!s) return null;
  const m = /^(\d{1,6})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return NaN;
  return Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0') || 0);
}
export const krFromOre = (ore: number | null | undefined) => (ore === null || ore === undefined ? '' : (ore % 100 ? (ore / 100).toFixed(2).replace('.', ',') : String(ore / 100)));

/** The bookings that collide with what is typed (date, times, place) — asked half a second after the typing stops. */
export function useConflicts(startsAt: string | null, endsAt: string | null, place: string, except?: string): string {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!startsAt || !place.trim()) { setText(''); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      const q = new URLSearchParams({ startsAt, location: place.trim(), ...(endsAt ? { endsAt } : {}), ...(except ? { except } : {}) });
      call(`/events/conflicts?${q}`).then((r) => { if (live) setText(r.message_no || ''); }).catch(() => { if (live) setText(''); });
    }, 500);
    return () => { live = false; clearTimeout(t); };
  }, [startsAt, endsAt, place, except]);
  return text;
}

/** The amber line under the time and place. Nothing when there is no conflict. */
export function ConflictNote({ text }: { text: string }) {
  return text ? <p className="msg warn" role="status">{text} Du kan lagre likevel.</p> : null;
}

/** «Gjester»: contacts invited to the event. Adding one is logged as a touch in their contact log when that is on. */
export function EventGuests({ event }: { event: Body }) {
  const [data, setData] = useState<Body | null>(null);
  const [q, setQ] = useState('');
  const [pick, setPick] = useState('');
  const [role, setRole] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, say] = useMsg();
  const base = `/events/${encodeURIComponent(event.id)}/guests`;
  const load = async (query = q) => {
    try { setData(await call(`${base}${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`)); }
    catch (x) { say(`Kunne ikke hente gjestene: ${(x as Error).message}`, 'err'); }
  };
  const after = (r: { ok: boolean; body: Body }, done: string) => {
    if (!r.ok) { say(errText(r.body, { contactId: 'Kontakt', roleTitle: 'Rolle' }), 'err'); return; }
    setData({ ...data, items: r.body.items }); say(done, 'ok');
  };
  const add = async () => {
    if (!pick || busy) return;
    setBusy(true);
    try {
      const r = await post(base, { contactId: pick, roleTitle: role.trim() || null, showInApp: show });
      after(r, r.body.touched ? 'Gjesten er lagt til, og invitasjonen er ført i kontaktloggen.' : 'Gjesten er lagt til.');
      if (r.ok) { setPick(''); setRole(''); setShow(false); void load(); }
    } finally { setBusy(false); }
  };
  const editable = !['cancelled', 'archived'].includes(event.status);
  // PM gaps B11: a guest chosen (or a role typed) and not added yet — leaving the page asks first.
  useDirty(`guests-${event.id}`, Boolean(pick || role.trim()));
  return <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open && !data) void load(); }}>
    <summary>Gjester{data && data.items.length ? ` (${data.items.length})` : ''}</summary>
    {!data ? <p className="note" role="status">Henter …</p> : <>
      {data.items.length ? <table className="t">
        <thead><tr><th scope="col">Gjest</th><th scope="col">Rolle</th><th scope="col">Vis i appen</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
        <tbody>{data.items.map((g: Body) => (
          <tr key={g.id}>
            <td>{g.name}{g.archived && <> <Tag tone="grey">Arkivert</Tag></>}</td>
            <td><input aria-label={`Rolle for ${g.name}`} defaultValue={g.roleTitle || ''} maxLength={80} disabled={!editable}
              onBlur={async (e) => { const v = e.target.value.trim(); if (v !== (g.roleTitle || '')) after(await patch(`${base}/${g.id}`, { roleTitle: v || null }), 'Rollen er lagret.'); }} /></td>
            <td><input type="checkbox" aria-label={`Vis ${g.name} i appen`} checked={g.showInApp} disabled={!editable}
              onChange={async (e) => after(await patch(`${base}/${g.id}`, { showInApp: e.target.checked }), e.target.checked ? 'Vises i appen.' : 'Vises ikke i appen.')} /></td>
            {/* PM gaps B13: two clicks, as «Ta ned» on Varsler — the second says what goes. */}
            <td>{editable && <TwoStep label="Fjern" ariaLabel={`Fjern ${g.name} fra gjestene`} confirmLabel="Klikk igjen: fjernes fra gjestene"
              onConfirm={async () => after(await send(`${base}/${g.id}`, { method: 'DELETE' }), 'Gjesten er fjernet.')} />}</td>
          </tr>))}</tbody>
      </table> : <p className="note">Ingen gjester ennå.</p>}
      {editable && <>
        <div className="row">
          <Field label="Søk i Kontakter" className="field half">{(id) => <input id={id} value={q} onChange={(e) => { setQ(e.target.value); void load(e.target.value); }} placeholder="Navn eller rolle" />}</Field>
          <Field label="Kontakt" className="field half">{(id) => (
            <select id={id} value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Velg …</option>
              {(data.candidates || []).map((c: Body) => <option key={c.id} value={c.id}>{c.name}{c.roleTitle ? ` — ${c.roleTitle}` : ''}</option>)}
            </select>)}</Field>
          <Field label="Rolle på arrangementet" note="F.eks. «Naat», «Hovedtaler»." className="field half">{(id, d) => <input id={id} aria-describedby={d} value={role} maxLength={80} onChange={(e) => setRole(e.target.value)} />}</Field>
        </div>
        <label className="check"><input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Vis i appen (bare navn og rolle — aldri telefon eller e-post)</label>
        <div className="row"><Btn ghost disabled={!pick || busy} onClick={add}>Legg til gjest</Btn></div>
        {data.contactLog && <p className="note">Invitasjonen føres i kontaktens kontaktlogg.</p>}
      </>}
    </>}
  </details>;
}

/** The survey after the event: the average and the comments — «Deltaker», never a name. */
export function EventFeedback({ event, summary }: { event: Body; summary?: Body | null }) {
  const [data, setData] = useState<Body | null>(null);
  const [, say] = useMsg();
  const label = summary && summary.count ? `★ ${String(Number(summary.average).toFixed(1)).replace('.', ',')} (${summary.count})` : '';
  return <details onToggle={async (e) => {
    if (!(e.target as HTMLDetailsElement).open || data) return;
    try { setData(await call(`/events/${encodeURIComponent(event.id)}/feedback`)); } catch (x) { say(`Kunne ikke hente tilbakemeldingene: ${(x as Error).message}`, 'err'); }
  }}>
    <summary>Tilbakemeldinger{label ? ` ${label}` : ''}</summary>
    {!data ? <p className="note" role="status">Henter …</p> : !data.summary.count ? <p className="note">Ingen har svart ennå. Appen spør dagen etter, kl. 10.</p> : <>
      <p>{data.summary.label_no} — snitt av {data.summary.count} svar.</p>
      {data.comments.length ? <ul>{data.comments.map((c: Body, i: number) => <li key={i}><b>Deltaker</b> ({'★'.repeat(c.stars)}): {c.text}</li>)}</ul>
        : <p className="note">Ingen kommentarer.</p>}
    </>}
  </details>;
}

/** «Billetter» for one event (Påmelding): who bought (names, logged), how many, and check-in at the door. */
export function TicketList({ eventId }: { eventId: string }) {
  const [data, setData] = useState<Body | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [, say] = useMsg();
  const base = `/events/${encodeURIComponent(eventId)}/tickets`;
  const [hidden, setHidden] = useState(false);
  const load = async () => {
    try { setData(await call(base)); }
    catch (x) { if ((x as { status?: number }).status === 403 || (x as { status?: number }).status === 404) setHidden(true); else say(`Kunne ikke hente billettene: ${(x as Error).message}`, 'err'); }
  };
  useEffect(() => { void load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps
  const mark = async (o: Body, checkedIn: boolean) => {
    if (busy) return;
    setBusy(o.orderId);
    try {
      const r = await send(`${base}/${encodeURIComponent(o.orderId)}/checkin`, { method: 'PUT', body: JSON.stringify({ checkedIn }) });
      if (!r.ok) { say(errText(r.body), 'err'); return; }
      say(checkedIn ? `Ordre ${o.orderNo} er sjekket inn.` : `Innsjekkingen av ordre ${o.orderNo} er angret.`, 'ok');
      void load();
    } finally { setBusy(null); }
  };
  if (hidden) return null;
  if (!data) return <p className="note" role="status">Henter billetter …</p>;
  return <section aria-label="Billetter">
    <h3>Billetter</h3>
    <p>{data.totals.tickets} billetter i {data.totals.orders} ordrer · {data.totals.checkedIn} sjekket inn</p>
    {data.items.length ? <table className="t">
      <thead><tr><th scope="col">Ordre</th><th scope="col">Navn</th><th scope="col">Antall</th><th scope="col">Status</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
      <tbody>{data.items.map((o: Body) => (
        <tr key={o.orderId}>
          <td>{o.orderNo}</td><td>{o.name || 'Uten navn'}</td><td>{o.quantity}</td>
          <td>{o.checkedInAt ? <Tag tone="green">Kommet</Tag> : <Tag tone="grey">Ikke kommet</Tag>}</td>
          <td>{data.can.checkin && (o.checkedInAt
            ? <Btn ghost disabled={busy === o.orderId} onClick={() => mark(o, false)}>Angre</Btn>
            : <Btn disabled={busy === o.orderId} onClick={() => mark(o, true)}>Sjekk inn</Btn>)}</td>
        </tr>))}</tbody>
    </table> : <p className="note">Ingen billetter er solgt ennå.</p>}
    <p className="note">Refusjon gjøres på ordren under Butikk. Pengene trekkes når billetten er betalt.</p>
  </section>;
}

/** The ticket line for an event row: «Billett 150 kr · 30 av 100 solgt». */
export const ticketText = (e: Body, sold: number | undefined) =>
  e.price_ore ? `Billett ${kr(e.price_ore / 100)}${sold !== undefined ? ` · ${sold}${e.ticket_capacity ? ` av ${e.ticket_capacity}` : ''} solgt` : ''}` : '';
